import { lerpColor } from '../../color';
import { hsl, type World as Look } from '../../world';
import { calmAt, calmCenter, glowDot, grain, rgba, spots, starPath, vignette, type Painter } from '../../art/paint';

const TAU = Math.PI * 2;
const BALL_ROWS = 9;
const BALL_COLS = 12;
const BALL_GLINTS = [-0.42, -0.45, 0.42, 0.36, -0.22, 0.24, -0.12, 0.42, 0.18, 0.8, -0.72, 0.3];
const FLOOR_ROWS = [0, 0.16, 0.38, 0.66, 1];
const BALLOONS_WIDE = [0.9, 0.34, 0.95, 0.43, 0.865, 0.48];
const BALLOONS_TALL = [0.86, 0.27, 0.93, 0.32, 0.79, 0.33];

interface Tones {
  readonly bg: number;
  readonly far: number;
  readonly ceil: number;
  readonly ink: number;
  readonly rim: number;
  readonly path: number;
  readonly l: number;
  readonly a1: number;
  readonly a2: number;
}

interface Layout {
  readonly portrait: boolean;
  readonly floor: number;
  readonly base: number;
  readonly s: number;
  readonly ball: { readonly x: number; readonly y: number; readonly r: number };
}

function tonesOf(look: Look): Tones {
  const pal = look.palette;
  return {
    bg: pal.bg,
    far: hsl(look.hue, 36, 16),
    ceil: look.ceil,
    ink: pal.ink,
    rim: pal.rim,
    path: pal.path,
    l: pal.incoming.circle,
    a1: pal.held,
    a2: pal.spot,
  };
}

function layoutOf(p: Painter): Layout {
  const portrait = p.w / p.h < 0.8;
  const r = p.unit * (portrait ? 0.17 : 0.15);
  const ball = portrait
    ? { x: p.w * 0.24, y: p.h * 0.26, r }
    : { x: Math.max(r * 1.7, Math.min(p.w * 0.14, p.cx - p.unit * 1.12)), y: Math.max(p.h * 0.2 + r * 1.5, p.h * 0.3), r };
  return { portrait, floor: p.h * (portrait ? 0.77 : 0.79), base: p.h * (portrait ? 0.875 : 0.88), s: p.unit * (portrait ? 0.85 : 1), ball };
}

function box(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function disc(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
}

function wall(p: Painter, t: Tones): void {
  const g = p.ctx.createLinearGradient(0, 0, 0, p.h);
  g.addColorStop(0, rgba(lerpColor(t.bg, t.ink, 0.55)));
  g.addColorStop(0.55, rgba(t.bg));
  g.addColorStop(1, rgba(t.far));
  p.ctx.fillStyle = g;
  p.ctx.fillRect(0, 0, p.w, p.h);
}

function floor(p: Painter, t: Tones, lay: Layout, lit: boolean): void {
  const ctx = p.ctx;
  const top = lay.floor;
  const depth = p.h - top;
  const vy = top - depth * 1.6;
  const cols = lay.portrait ? 7 : 11;
  const span = p.w * 1.7;
  const xAt = (j: number, y: number): number => {
    const bx = p.cx - span / 2 + (span * j) / cols;
    return p.cx + ((bx - p.cx) * (y - vy)) / (p.h - vy);
  };
  for (let i = 0; i < FLOOR_ROWS.length - 1; i++) {
    const y0 = top + FLOOR_ROWS[i]! * depth;
    const y1 = top + FLOOR_ROWS[i + 1]! * depth;
    for (let j = 0; j < cols; j++) {
      let tone = lerpColor(t.far, t.ceil, p.rng.range(0.1, 0.32) + ((i + j) % 2) * 0.14);
      if (lit && p.rng.chance(0.18)) tone = lerpColor(t.far, p.rng.pick([t.a1, t.a2, t.l]), 0.13);
      ctx.fillStyle = rgba(tone);
      ctx.beginPath();
      ctx.moveTo(xAt(j, y0), y0);
      ctx.lineTo(xAt(j + 1, y0), y0);
      ctx.lineTo(xAt(j + 1, y1), y1);
      ctx.lineTo(xAt(j, y1), y1);
      ctx.closePath();
      ctx.fill();
    }
  }
  ctx.strokeStyle = rgba(t.ink, 0.28);
  for (let j = 0; j <= cols; j++) {
    ctx.lineWidth = p.rng.range(1, 2.2);
    ctx.beginPath();
    ctx.moveTo(xAt(j, top), top);
    ctx.lineTo(xAt(j, p.h), p.h);
    ctx.stroke();
  }
  for (const f of FLOOR_ROWS) {
    ctx.lineWidth = p.rng.range(1, 2.2);
    ctx.beginPath();
    ctx.moveTo(0, top + f * depth);
    ctx.lineTo(p.w, top + f * depth);
    ctx.stroke();
  }
  ctx.strokeStyle = rgba(lerpColor(t.far, t.ceil, 0.7), 0.3);
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, top);
  ctx.lineTo(p.w, top);
  ctx.stroke();
}

function speaker(p: Painter, t: Tones, x: number, base: number, w: number): void {
  const ctx = p.ctx;
  const h = w * 0.95;
  for (let i = 0; i < 2; i++) {
    const y = base - h * (i + 1);
    box(ctx, x, y, w, h - 3, w * 0.05);
    ctx.fillStyle = rgba(lerpColor(t.far, t.ceil, 0.3 - i * 0.08));
    ctx.fill();
    ctx.strokeStyle = rgba(t.ink, 0.5);
    ctx.lineWidth = 1.5;
    ctx.stroke();
    const cx = x + w / 2;
    const cy = y + h * 0.58;
    disc(ctx, cx, cy, w * 0.32);
    ctx.fillStyle = rgba(lerpColor(t.far, t.ink, 0.35));
    ctx.fill();
    ctx.strokeStyle = rgba(lerpColor(t.far, t.ceil, 0.7), 0.6);
    ctx.lineWidth = Math.max(1, w * 0.025);
    disc(ctx, cx, cy, w * 0.3);
    ctx.stroke();
    ctx.strokeStyle = rgba(lerpColor(t.far, t.ceil, 0.7), 0.3);
    disc(ctx, cx, cy, w * 0.18);
    ctx.stroke();
    disc(ctx, cx, cy, w * 0.07);
    ctx.fillStyle = rgba(lerpColor(t.far, t.ceil, 0.5));
    ctx.fill();
    disc(ctx, cx, y + h * 0.17, w * 0.08);
    ctx.fillStyle = rgba(lerpColor(t.far, t.ink, 0.3));
    ctx.fill();
    ctx.strokeStyle = rgba(t.ceil, 0.5);
    ctx.lineWidth = 1;
    ctx.stroke();
  }
}

function crates(p: Painter, t: Tones, x: number, base: number, w: number): void {
  const ctx = p.ctx;
  const h = w * 0.42;
  const top = base - h;
  const sq = h * 1.05;
  for (let i = 0; i < 5; i++) {
    const sx = x + w * (0.08 + i * 0.16) + sq / 2;
    const tilt = (i - 2) * 0.06 + p.rng.range(-0.04, 0.04);
    const tone = lerpColor(t.far, p.rng.pick([t.l, t.a1, t.a2, t.ceil]), p.rng.range(0.14, 0.24));
    ctx.save();
    ctx.translate(sx, top + h * 0.35);
    ctx.rotate(tilt);
    ctx.fillStyle = rgba(tone);
    ctx.fillRect(-sq / 2, -sq, sq, sq);
    ctx.strokeStyle = rgba(t.ink, 0.5);
    ctx.lineWidth = 1;
    ctx.strokeRect(-sq / 2, -sq, sq, sq);
    disc(ctx, 0, -sq * 0.55, sq * 0.24);
    ctx.fillStyle = rgba(lerpColor(tone, t.rim, 0.15), 0.5);
    ctx.fill();
    ctx.restore();
  }
  const rx = x + w * 0.86;
  const ry = top - sq * 0.55;
  disc(ctx, rx, ry, sq * 0.42);
  ctx.fillStyle = rgba(t.ink);
  ctx.fill();
  ctx.strokeStyle = rgba(lerpColor(t.ink, t.ceil, 0.6), 0.6);
  ctx.lineWidth = 1;
  disc(ctx, rx, ry, sq * 0.3);
  ctx.stroke();
  disc(ctx, rx, ry, sq * 0.12);
  ctx.fillStyle = rgba(t.a2, 0.5);
  ctx.fill();
  box(ctx, x, top, w, h, w * 0.03);
  ctx.fillStyle = rgba(lerpColor(t.far, t.ceil, 0.45));
  ctx.fill();
  ctx.strokeStyle = rgba(t.ink, 0.55);
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.strokeStyle = rgba(t.ink, 0.35);
  ctx.beginPath();
  ctx.moveTo(x + w * 0.04, top + h * 0.38);
  ctx.lineTo(x + w * 0.96, top + h * 0.38);
  ctx.moveTo(x + w * 0.04, top + h * 0.7);
  ctx.lineTo(x + w * 0.96, top + h * 0.7);
  ctx.stroke();
  box(ctx, x + w * 0.4, top + h * 0.12, w * 0.2, h * 0.14, h * 0.07);
  ctx.fillStyle = rgba(t.ink, 0.6);
  ctx.fill();
}

function turntable(p: Painter, t: Tones, x: number, base: number, w: number): void {
  const ctx = p.ctx;
  const sh = w * 0.42;
  const st = base - sh;
  box(ctx, x + w * 0.04, st, w * 0.92, sh, w * 0.02);
  ctx.fillStyle = rgba(lerpColor(t.far, t.ceil, 0.35));
  ctx.fill();
  ctx.strokeStyle = rgba(t.ink, 0.5);
  ctx.lineWidth = 1.5;
  ctx.stroke();
  const dh = w * 0.12;
  const dt = st - dh;
  box(ctx, x, dt, w, dh, w * 0.02);
  ctx.fillStyle = rgba(lerpColor(t.far, t.ceil, 0.6));
  ctx.fill();
  ctx.stroke();
  disc(ctx, x + w * 0.1, dt + dh * 0.5, dh * 0.2);
  ctx.fillStyle = rgba(t.a1, 0.4);
  ctx.fill();
  disc(ctx, x + w * 0.18, dt + dh * 0.5, dh * 0.2);
  ctx.fillStyle = rgba(t.a2, 0.4);
  ctx.fill();
  const dd = w * 0.22;
  ctx.beginPath();
  ctx.moveTo(x, dt);
  ctx.lineTo(x + w, dt);
  ctx.lineTo(x + w * 0.94, dt - dd);
  ctx.lineTo(x + w * 0.06, dt - dd);
  ctx.closePath();
  ctx.fillStyle = rgba(lerpColor(t.far, t.ceil, 0.5));
  ctx.fill();
  ctx.strokeStyle = rgba(t.ink, 0.5);
  ctx.stroke();
  const px = x + w * 0.42;
  const py = dt - dd * 0.5;
  const pr = w * 0.3;
  ctx.beginPath();
  ctx.ellipse(px, py, pr, dd * 0.42, 0, 0, TAU);
  ctx.fillStyle = rgba(t.ink);
  ctx.fill();
  ctx.strokeStyle = rgba(lerpColor(t.ink, t.ceil, 0.6), 0.6);
  ctx.lineWidth = 1;
  for (const f of [0.75, 0.5]) {
    ctx.beginPath();
    ctx.ellipse(px, py, pr * f, dd * 0.42 * f, 0, 0, TAU);
    ctx.stroke();
  }
  ctx.beginPath();
  ctx.ellipse(px, py, pr * 0.22, dd * 0.42 * 0.22, 0, 0, TAU);
  ctx.fillStyle = rgba(t.a2, 0.5);
  ctx.fill();
  const ax = x + w * 0.86;
  const ay = dt - dd * 0.75;
  ctx.strokeStyle = rgba(t.path, 0.55);
  ctx.lineWidth = Math.max(1, w * 0.015);
  ctx.beginPath();
  ctx.moveTo(ax, ay);
  ctx.lineTo(x + w * 0.62, dt - dd * 0.3);
  ctx.stroke();
  disc(ctx, ax, ay, w * 0.035);
  ctx.fillStyle = rgba(lerpColor(t.ceil, t.path, 0.4));
  ctx.fill();
  box(ctx, x + w * 0.585, dt - dd * 0.36, w * 0.06, w * 0.03, w * 0.008);
  ctx.fill();
}

function balloons(p: Painter, t: Tones, lay: Layout): void {
  const ctx = p.ctx;
  const at = lay.portrait ? BALLOONS_TALL : BALLOONS_WIDE;
  const rx = p.unit * (lay.portrait ? 0.075 : 0.06);
  for (let i = 0; i < at.length; i += 2) {
    const x = at[i]! * p.w;
    const y = at[i + 1]! * p.h;
    const tone = p.rng.pick([t.a2, t.l, t.a1]);
    if (calmAt(p, x, y, 0.9) || Math.hypot(x - p.vanish.x, y - p.vanish.y) < 0.4 * p.unit) continue;
    ctx.strokeStyle = rgba(t.path, 0.3);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, y + rx * 1.3);
    ctx.bezierCurveTo(x + rx * 0.6, y + rx * 2.4, x - rx * 0.6, y + rx * 3.2, x + rx * 0.2, y + rx * 4.4);
    ctx.stroke();
    ctx.beginPath();
    ctx.ellipse(x, y, rx, rx * 1.2, p.rng.range(-0.2, 0.2), 0, TAU);
    ctx.fillStyle = rgba(lerpColor(t.far, tone, 0.45), 0.55);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(x, y + rx * 1.15);
    ctx.lineTo(x - rx * 0.16, y + rx * 1.38);
    ctx.lineTo(x + rx * 0.16, y + rx * 1.38);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(x - rx * 0.35, y - rx * 0.45, rx * 0.18, rx * 0.28, -0.5, 0, TAU);
    ctx.fillStyle = rgba(t.rim, 0.22);
    ctx.fill();
  }
}

function reflections(p: Painter, t: Tones, lay: Layout): void {
  const ctx = p.ctx;
  const b = lay.ball;
  for (const at of spots(p, 10, 0.95, 16, p.unit * 0.22)) {
    if (at.y > lay.floor || Math.hypot(at.x - b.x, at.y - b.y) < b.r * 2.2) continue;
    const s = p.unit * p.rng.range(0.012, 0.022);
    starPath(ctx, at.x, at.y, s, 0.22, 4, 0);
    ctx.fillStyle = rgba(p.rng.pick([t.l, t.a1, t.rim]), p.rng.range(0.25, 0.4));
    ctx.fill();
  }
}

function streamers(p: Painter, t: Tones, lay: Layout): void {
  const ctx = p.ctx;
  const xs = lay.portrait ? [0.06, 0.94] : [0.05, 0.27, 0.73, 0.95];
  const len = p.h * 0.17;
  ctx.lineWidth = Math.max(1.5, p.unit * 0.012);
  ctx.lineCap = 'round';
  for (const f of xs) {
    const x = f * p.w;
    if (Math.hypot(x - p.vanish.x, len * 0.5 - p.vanish.y) < 0.4 * p.unit) continue;
    const phase = p.rng.float() * TAU;
    ctx.strokeStyle = rgba(lerpColor(t.far, p.rng.pick([t.a1, t.a2, t.l]), 0.5), 0.45);
    ctx.beginPath();
    for (let i = 0; i <= 24; i++) {
      const k = i / 24;
      const px = x + Math.sin(k * TAU * 2 + phase) * p.unit * 0.025;
      if (i === 0) ctx.moveTo(px, 0);
      else ctx.lineTo(px, k * len);
    }
    ctx.stroke();
  }
}

function mirrorBall(p: Painter, t: Tones, lay: Layout): void {
  const ctx = p.ctx;
  const { x, y, r } = lay.ball;
  ctx.strokeStyle = rgba(lerpColor(t.path, t.bg, 0.45), 0.55);
  ctx.lineWidth = Math.max(1, r * 0.04);
  ctx.beginPath();
  ctx.moveTo(x, 0);
  ctx.lineTo(x, y - r * 1.08);
  ctx.stroke();
  box(ctx, x - r * 0.12, y - r * 1.12, r * 0.24, r * 0.14, r * 0.03);
  ctx.fillStyle = rgba(lerpColor(t.ceil, t.path, 0.25));
  ctx.fill();
  glowDot(ctx, x, y, r * 2.6, t.l, 0.14);
  ctx.save();
  disc(ctx, x, y, r);
  ctx.clip();
  ctx.fillStyle = rgba(t.far);
  ctx.fillRect(x - r, y - r, r * 2, r * 2);
  const pt = (la: number, lo: number): [number, number] => [x + r * Math.cos(la) * Math.sin(lo), y + r * Math.sin(la)];
  for (let i = 0; i < BALL_ROWS; i++) {
    const a0 = -Math.PI / 2 + (i * Math.PI) / BALL_ROWS;
    const a1 = a0 + Math.PI / BALL_ROWS;
    for (let j = 0; j < BALL_COLS; j++) {
      const o0 = -Math.PI / 2 + (j * Math.PI) / BALL_COLS;
      const o1 = o0 + Math.PI / BALL_COLS;
      const ml = (a0 + a1) / 2;
      const mo = (o0 + o1) / 2;
      const d = -0.5 * Math.cos(ml) * Math.sin(mo) - 0.55 * Math.sin(ml) + 0.67 * Math.cos(ml) * Math.cos(mo);
      const k = Math.max(0, Math.min(1, 0.3 + 0.9 * d + p.rng.range(-0.15, 0.15)));
      const tone = p.rng.chance(0.12)
        ? lerpColor(t.ceil, p.rng.pick([t.l, t.a1, t.rim]), 0.4)
        : lerpColor(t.far, lerpColor(t.ceil, t.l, 0.15), k);
      const c = [pt(a0, o0), pt(a0, o1), pt(a1, o1), pt(a1, o0)];
      ctx.beginPath();
      c.forEach(([px, py], n) => {
        if (n === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      });
      ctx.closePath();
      ctx.fillStyle = rgba(tone);
      ctx.fill();
    }
  }
  ctx.strokeStyle = rgba(t.ink, 0.45);
  ctx.lineWidth = Math.max(0.8, r * 0.02);
  ctx.beginPath();
  for (let i = 1; i < BALL_ROWS; i++) {
    const la = -Math.PI / 2 + (i * Math.PI) / BALL_ROWS;
    const half = r * Math.cos(la);
    ctx.moveTo(x - half, y + r * Math.sin(la));
    ctx.lineTo(x + half, y + r * Math.sin(la));
  }
  for (let j = 1; j < BALL_COLS; j++) {
    const lo = -Math.PI / 2 + (j * Math.PI) / BALL_COLS;
    for (let s = 0; s <= 16; s++) {
      const [px, py] = pt(-Math.PI / 2 + (s * Math.PI) / 16, lo);
      if (s === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
  }
  ctx.stroke();
  const sh = ctx.createRadialGradient(x - r * 0.35, y - r * 0.4, r * 0.1, x, y, r);
  sh.addColorStop(0, rgba(t.ink, 0));
  sh.addColorStop(0.7, rgba(t.ink, 0.15));
  sh.addColorStop(1, rgba(t.ink, 0.55));
  ctx.fillStyle = sh;
  ctx.fillRect(x - r, y - r, r * 2, r * 2);
  ctx.restore();
  ctx.strokeStyle = rgba(lerpColor(t.ceil, t.rim, 0.25), 0.55);
  ctx.lineWidth = Math.max(1, r * 0.03);
  disc(ctx, x, y, r);
  ctx.stroke();
  for (let i = 0; i < BALL_GLINTS.length; i += 3) {
    const gx = x + BALL_GLINTS[i]! * r;
    const gy = y + BALL_GLINTS[i + 1]! * r;
    const s = BALL_GLINTS[i + 2]! * r;
    glowDot(ctx, gx, gy, s * 1.3, t.a1, 0.22);
    starPath(ctx, gx, gy, s, 0.18, 4, 0);
    ctx.fillStyle = rgba(t.rim, 0.6);
    ctx.fill();
  }
}

export function paint(p: Painter, look: Look): void {
  const t = tonesOf(look);
  const lay = layoutOf(p);
  const sw = lay.s * (lay.portrait ? 0.18 : 0.3);
  const cw = lay.s * 0.42;
  const tw = lay.s * 0.46;
  wall(p, t);
  floor(p, t, lay, p.level >= 3);
  speaker(p, t, p.w * 0.03, lay.floor + p.h * 0.02, sw);
  speaker(p, t, p.w * 0.97 - sw, lay.floor + p.h * 0.02, sw);
  turntable(p, t, p.w * 0.97 - tw, lay.base, tw);
  crates(p, t, p.w * 0.03, lay.base, cw);
  balloons(p, t, lay);
  if (p.level >= 2) reflections(p, t, lay);
  if (p.level >= 4) streamers(p, t, lay);
  mirrorBall(p, t, lay);
  calmCenter(p, t.bg, 0.62, 1.15);
  grain(p, 0.05, true);
  vignette(p, 0x000000, 0.45);
}
